/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { Button } from "@nordcloud/gnui";
import { Resource } from "~/generated/graphql";
import { SidebarSelector } from "~/components/SidebarWithMultiSelect/SidebarSelector";
import { useDisclosure, useGetResources } from "~/hooks";
import { mapResourceToSelectedItem } from "~/views/resourceGroups/utils";
import { ResourceSidebarItem } from ".";

type Props = {
  handleManualSubmit: (ids: string[]) => void;
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
  setAllResources: React.Dispatch<React.SetStateAction<Resource[]>>;
};

export function ResourceSidebar({
  handleManualSubmit,
  selectedOptions,
  setSelectedOptions,
  setAllResources,
}: Props) {
  const { isOpen, close, open } = useDisclosure();

  const { resources, getResources, loading } = useGetResources();
  const [allResourcesCurrent, setAllResourcesCurrent] =
    useState<string[]>(selectedOptions);

  const handleItemSelect = (selectedItem: string) => {
    const isSelected = allResourcesCurrent.includes(selectedItem);

    setAllResourcesCurrent((selectedItems) =>
      isSelected
        ? selectedItems.filter((item) => item !== selectedItem)
        : [...selectedItems, selectedItem]
    );
  };

  const handleSubmit = (data: string[]) => {
    setSelectedOptions(data);
    handleManualSubmit(data);
    close();
  };

  const handleClose = () => {
    close();
  };

  const handleOpenSidebar = () => {
    setAllResourcesCurrent(selectedOptions);
    getResources();
    open();
  };

  useEffect(() => {
    if (isOpen) {
      setAllResources(resources);
    }
  }, [isOpen, resources, setAllResources]);

  return (
    <>
      <Button
        ml="auto"
        severity="low"
        type="button"
        icon="plus"
        onClick={handleOpenSidebar}
      >
        Add Manually
      </Button>
      <SidebarSelector
        name="resource"
        title="Select Resources"
        sidebarItem={ResourceSidebarItem}
        isOpen={isOpen}
        onClose={handleClose}
        showValueInOptions
        options={resources.map(mapResourceToSelectedItem)}
        selectedOptions={allResourcesCurrent}
        onItemSelect={handleItemSelect}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </>
  );
}
